import { NavigationState } from "react-native-tab-view";
import { JsonMap, SegmentClient } from "@segment/analytics-react-native";
import React from "react";
import { Dimensions, Platform } from "react-native";
import { getContext } from "./web-properties";
import {
  getDeviceType,
  getProductNum,
} from "@components/molecules/AdView/BannerAd/utils";
import { getUserAnalyticsPayload } from "../hooks/useAnalytics";
import { preLoginRoutes } from "./product/productValidators";
import Routes from "@navigations/Routes";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const prepareScreenEvent = (routeName: string, context: any) => {
  const { height } = Dimensions.get("window");
  const { deviceWidth: width } = useIsFoldable();
  const userAnalyticsPayload = getUserAnalyticsPayload();
  return {
    name: routeName,
    path: context.page.path || "",
    url: context.page.url || "",
    userAgent: context.userAgent,
    locale: context.locale,
    context: {
      platform: Platform.OS,
    },
    brand: context.browserInfo.browser,
    version: context.browserInfo.version,
    platform: context.browserInfo.platform,
    mobile: context.browserInfo.mobile,
    timezone: context.timezone,
    device_type_enum: getDeviceType(Platform.OS).toString(),
    deviceResolution: `${width}x${height}`,
    ...userAnalyticsPayload,
  };
};

export const getActiveRouteName = (
  state: NavigationState<any> | undefined
): string => {
  if (!state || !state.routes || state.routes.length === 0) {
    return "Unknown";
  }

  const route = state?.routes[state?.index ?? 0];
  if (
    route?.name === Routes.ExploreNavigator &&
    route?.params?.screen === Routes.AccountOverviewScreen
  ) {
    return Routes.AccountOverviewScreen;
  }
  if (route?.state) {
    return getActiveRouteName(route.state);
  }

  if (route?.params?.screen) {
    return route.params.screen;
  }

  return route?.name || "Unknown";
};

export const getNavigationAnalytics = (
  state: NavigationState<any> | undefined,
  routeName: React.MutableRefObject<string>,
  segmentClient: SegmentClient
  // userAnalyticsPayload: UserAnalyticsData
) => {
  const newRouteName = getActiveRouteName(state);
  const segmentUserInfo = segmentClient?.userInfo.get()
    ? segmentClient.userInfo.get()
    : {};

  setTimeout(() => {
    const screenEventData = prepareScreenEvent(newRouteName, getContext());

    if (routeName.current !== newRouteName) {
      if (!preLoginRoutes.includes(newRouteName)) {
        if (!segmentUserInfo.userId) {
          console.error(
            "Screen Event tracked without user:",
            newRouteName,
            JSON.stringify(screenEventData)
          );
        }
        segmentClient?.screen(newRouteName, screenEventData).then();
      }
      routeName.current = newRouteName;
    }
  }, 500);
};

interface TrackData extends JsonMap {
  oid: string;
  app_name: string;
  app_version: string;
}

export interface UserAnalyticsData extends TrackData {
  user_company_id: string;
  user_company_name: string;
  user_email: string;
  user_id: string;
  user_name: string;
  user_organization_id: string;
  user_organization_name: string;
  user_price_mode: string;
}

export interface ItemAnalyticsData extends JsonMap {
  product_company_id: string;
  product_company_name: string;
  product_id: string;
  product_name: string;
  product_stock_num: string;
  product_type: string;
  product_type_enum: string;
  vendor_id: string;
  vendor_address: string;
  vendor_city: string;
  vendor_contact: string;
  vendor_country: string;
  vendor_email: string;
  vendor_location_name: string;
  vendor_mobile: string;
  vendor_office: string;
  vendor_state: string;
  vendor_zip: string;
  product_sub_type: string;
}

export const appConfig: TrackData = {
  oid: process.env.EXPO_PUBLIC_ORGANIZATION_ID,
  app_name: process.env.APP_NAME,
  // TODO: - replace with the actual version of the app
  app_version: "2.0",
};

export enum EventName {
  BUTTON_CLICK = "button_click",
  DETAILS_CONTACT_BUTTON_CLICK = "Detail Contact Button Click",
  CONTACT_VENDOR_THROUGH_CALL = "Contact Vendor Through Call",
  HYPER_LINK_CLICK = "hyper_link_click",
  VIEW = "view",
}

export enum EventType {
  SHARE_VIA_EMAIL = "share_via_email",
  SHARE_VIA_APP = "share_via_app",
  SHARE_VIA_LINK = "share_via_link",
  ASK_SUPPLIER = "ask_supplier",
  MEMO = "memo",
  BUY = "buy",
  HOLD = "hold",
  MATCH_PAIR = "match_pair",
  DETAIL_CONTACT_BUTTON = "detail_contact_button",
  VIEW_BANNER = "View Banner Business_demo",
  VIEW_PROFILE_POP_UP = "go_to_profile",
  CLOSE_PROFILE_POP_UP = "close_pop_up",
  BANNER_LINK_CLICK = "banner_link_click",
  ACCOUNT_OVERVIEW_TAB_CLICK = "account_overview_tab_click",
  USER_SEATS_TAB_CLICK = "user_seats_tab_click",
  COMPANY_DETAILS_TAB_CLICK = "company_details_tab_click",
  MANAGE_PLAN_BUTTON_CLICK = "manage_plan_button_click",
  USER_SEATS_BUTTON_CLICK = "manage_users_button_click",
  PLAN_1_SELECTED_BUTTON_CLICK = "plan_1_selected_button_click",
  PLAN_2_SELECTED_BUTTON_CLICK = "plan_2_selected_button_click",
  CONFIRM_PLAN_BUTTON_CLICK = "confirm_plan_button_click",
  CONTACT_US_BUTTON_CLICK = "contact_us_button_click",
  PURCHASE_SEATS_BUTTON_CLICK = "purchase_seats_button_click",
  PURCHASE_SEATS_CONFIRM_BUTTON_CLICK = "purchase_seats_confirm_button_click",
  ADD_MORE_USERS_BUTTON_CLICK = "add_more_users_button_click",
  INVITE_USERS_BUTTON_CLICK = "invite_users_button_click",
  INVITE_USERS_CONFIRM_BUTTON_CLICK = "invite_users_confirm_button_click",
  INVITE_USERS_CANCEL_BUTTON_CLICK = "invite_users_cancel_button_click",
  REMOVE_USER_BUTTON_CLICK = "remove_user_button_click",
  MAKE_ADMIN_BUTTON_CLICK = "make_admin_button_click",
  MAKE_MEMBER_BUTTON_CLICK = "make_member_button_click",
  GRANT_ACCESS_TO_USER_BUTTON_CLICK = "grant_access_to_user_button_click",
  LIMIT_ACCESS_TO_USER_BUTTON_CLICK = "limit_access_to_user_button_click",
  CONTACT_VENDOR_THROUGH_CALL = "contact_vendor_through_call",
}

export enum ViewTypeEnum {
  PAID_MARKETPLACE_POP_UP = "paid_marketplace_pop_up",
  PAID_MARKETPLACE_BANNER = "paid_marketplace_banner",
  ACCOUNT_OVERVIEW_SCREEN_VIEW = "account_overview_screen_view",
  USER_SEATS_SCREEN_VIEW = "user_seats_screen_view",
  COMPANY_DETAILS_SCREEN_VIEW = "company_details_screen_view",
  INVITE_USER_POP_UP = "invite_user_pop_up",
  MANAGE_PLAN_POP_UP = "manage_plan_pop_up",
  PURCHASE_SEATS_POP_UP = "purchase_seats_pop_up",
}

export enum ViewType {
  SCREEN_VIEW = "screen_view",
  POP_UP = "pop_up",
  BANNER = "banner",
}

export enum PriceModeEventType {
  CHANGE_PRICE_MODE = "change_price_mode",
  ADD_MARKUP = "add_markup",
  ORANGE_MODE_MARKUP = "orange_mode_locked_markup_access",
}

export enum CurrencyEventType {
  CHANGE_CURRENCY = "change_currency",
  HAS_CURRENCY_AVAILABLE = "has_currency_available",
}

export enum TermsOfUseEventType {
  CLICK_TERMS_OF_USE = "click_terms_of_use",
}

export enum ContactUsEventType {
  CLICK_CONTACT_US = "click_contact_us",
  SUBMIT_CONTACT_US_ERROR = "submit_contact_us_error",
  SUBMIT_CONTACT_US_SUCCESS = "submit_contact_us_success",
  OPEN_CONTACT_US_PAGE = "open_contact_us_page",
}

export enum SignOutEventType {
  CLICK_SIGN_OUT = "click_sign_out",
  SIGN_OUT_ERROR = "sign_out_error",
  SIGN_OUT_SUCCESS = "sign_out_success",
  CANCEL_SIGN_OUT = "cancel_sign_out",
}

export const getAnalyticsProductData = (item: any, productType?: any) => {
  const locations = item?.locations?.[0];
  const context = getContext();
  const { height } = Dimensions.get("window");
  const { deviceWidth: width } = useIsFoldable();
  const productBasedRoutes: Array<[number, string]> = [
    [0, "diamond"],
    [1, "lab_grown_diamond"],
    [1, "lab_grown_diamonds"],
    [2, "gemstone"],
    [3, "jewelry"],
    [4, "home"],
  ];

  const isProductBasedRoute = (path: string): number | boolean => {
    for (const [number, route] of productBasedRoutes) {
      if (path.includes(route)) {
        return number;
      }
    }
    return 0;
  };
  const result = isProductBasedRoute(context.page.path);

  const productData: ItemAnalyticsData = {
    product_company_id: item?.locations?.vendor_id,
    product_company_name: item?.vendor_name,
    product_id: item?.id?.toString(),
    product_name: item?.long_title,
    product_stock_num: item?.stock_num?.toString(),
    product_type: productType ? productType : item?.type,
    product_type_enum: item?.product_type
      ? getProductNum(item?.product_type)
        ? getProductNum(item?.product_type)
        : result
      : result,
    vendor_id: item?.vendor_id,
    vendor_address: item?.vendor_street_address,
    vendor_city: item?.vendor_city,
    vendor_contact: locations?.contact_person,
    vendor_country: item?.vendor_country,
    vendor_email: item?.vendor_email,
    vendor_location_name: locations?.location_name,
    vendor_mobile: item?.vendor_iphone,
    vendor_office: locations?.office_phone,
    vendor_state: item?.vendor_state,
    vendor_zip: item?.vendor_zip_code,
    device_type_enum: getDeviceType(Platform.OS).toString(),
    ...(item?.sub_type && { product_sub_type: item?.sub_type }),
    path: context.page.path || "",
    url: context.page.url || "",
    userAgent: context.userAgent,
    locale: context.locale,
    context: {
      platform: Platform.OS,
    },
    brand: context.browserInfo.browser,
    version: context.browserInfo.version,
    platform: context.browserInfo.platform,
    mobile: context.browserInfo.mobile,
    timezone: context.timezone,
    deviceResolution: `${width}x${height}`,
  };
  return productData;
};
